import type { NotificationType } from "@/lib/notifications/routing"

// ============================================================
// What a notification SAYS, decided on the server.
// ------------------------------------------------------------
// Until now a client passed the finished title and message straight into the
// table. Combined with two INSERT policies reading WITH CHECK (true), that
// meant any signed-in account could put any words in front of any user — and
// once the trigger started turning rows into pushes, onto their lock screen.
//
// So the client no longer sends words. It sends a TYPE and a few IDs, and the
// text is built here. The only thing a caller influences is which template
// runs and which records it names, and both are checked against what that
// caller is actually allowed to touch.
//
// NAMES ARE RESOLVED SERVER-SIDE, never passed in. A caller supplying
// `barberName` is a caller supplying text, which is the thing being removed —
// "Marcus" and "Marcus (ACCOUNT SUSPENDED — call 555-0100)" are equally
// strings. api/notifications/create looks the name up from the id instead.
// ============================================================

/** The pieces a template is allowed to interpolate. All resolved server-side. */
export interface TemplateContext {
  /** Display name of whoever caused this, from the database. */
  actorName?: string
  /** Shop name, from the database. */
  shopName?: string
  /** Job title, from the database. */
  jobTitle?: string
  /** Plan description, e.g. "weekly". From the database. */
  planName?: string
  /** A money amount already formatted, e.g. "$250.00". Built from a number. */
  amount?: string
}

export interface NotificationText {
  title: string
  message: string
}

type Template = (ctx: TemplateContext) => NotificationText

const someone = (ctx: TemplateContext) => ctx.actorName || "Someone"
const theShop = (ctx: TemplateContext) => ctx.shopName || "the shop"

/**
 * One template per type a client is allowed to send. A type missing from here
 * cannot be sent by a client at all — which is the point: a new client-side
 * notification has to be designed, not improvised at a call site.
 */
export const TEMPLATES: Partial<Record<NotificationType, Template>> = {
  booth_request: (c) => ({
    title: "New booth request",
    message: `${someone(c)} asked to rent a booth at ${theShop(c)}.`,
  }),
  booth_approved: (c) => ({
    title: "Booth request approved",
    message: `Your request at ${theShop(c)} was approved.`,
  }),
  booth_rejected: (c) => ({
    title: "Booth request declined",
    message: `Your request at ${theShop(c)} was declined.`,
  }),
  plan_offer: (c) => ({
    title: "New plan offer",
    message: c.planName
      ? `${theShop(c)} offered you the ${c.planName} plan.`
      : `${theShop(c)} sent you a plan offer.`,
  }),
  payment_reminder: (c) => ({
    title: "Payment reminder",
    message: c.amount
      ? `${c.amount} is due for ${theShop(c)}.`
      : `A payment is due for ${theShop(c)}.`,
  }),
  invite_to_apply: (c) => ({
    title: "Invited to apply",
    message: c.jobTitle
      ? `${theShop(c)} invited you to apply for ${c.jobTitle}.`
      : `${theShop(c)} invited you to apply.`,
  }),
  invitation_accepted: (c) => ({
    title: "Invitation accepted",
    message: `${someone(c)} accepted your invitation.`,
  }),
  invitation_declined: (c) => ({
    title: "Invitation declined",
    message: `${someone(c)} declined your invitation.`,
  }),
  job_inquiry: (c) => ({
    title: "Someone asked about your post",
    message: c.jobTitle
      ? `${someone(c)} asked about ${c.jobTitle}.`
      : `${someone(c)} asked about your job post.`,
  }),
  message: (c) => ({
    title: "New message",
    message: `${someone(c)} sent you a message.`,
  }),
  new_message: (c) => ({
    title: "New message",
    message: c.shopName
      ? `${someone(c)} sent you a message about ${c.shopName}.`
      : `${someone(c)} sent you a message.`,
  }),
  new_follower: (c) => ({
    title: "New follower",
    message: `${someone(c)} started following you.`,
  }),
  new_comment: (c) => ({
    title: "New comment",
    message: `${someone(c)} commented on your work.`,
  }),
  new_upvote: (c) => ({
    title: "New like",
    message: `${someone(c)} liked your work.`,
  }),
  plan_offer_accepted: (c) => ({
    title: "Plan offer accepted",
    message: `${someone(c)} accepted your plan offer.`,
  }),
  plan_offer_declined: (c) => ({
    title: "Plan offer declined",
    message: `${someone(c)} declined your plan offer.`,
  }),
  profile_view: () => ({
    title: "Profile viewed",
    message: "Someone viewed your profile.",
  }),
}

/** Whether a client may ask for this type at all. */
export function isClientSendable(type: string): type is NotificationType {
  return type in TEMPLATES
}

/**
 * Build the text. The caller has already been authorised; this only decides
 * wording.
 *
 * Every interpolated value is clipped — a display name is still user-controlled
 * input, just a much narrower kind than a whole message, and a 400-character
 * "name" should not be able to push the real words off a lock screen.
 */
export function renderNotification(type: NotificationType, context: TemplateContext): NotificationText {
  const template = TEMPLATES[type]
  if (!template) return { title: "BOOTH", message: "You have a new notification." }
  const clipped: TemplateContext = Object.fromEntries(
    Object.entries(context).map(([k, v]) => [k, typeof v === "string" ? v.slice(0, 60) : v]),
  )
  const { title, message } = template(clipped)
  return { title: title.slice(0, 80), message: message.slice(0, 240) }
}
